'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form/form-field';
import { MessageBar } from '@/components/ui/message-bar';
import styles from './sso-form.module.css';

const SsoSchema = z.object({
  email: z.string().email('Please enter a valid email'),
});
type SsoValues = z.infer<typeof SsoSchema>;

export function SsoForm() {
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SsoValues>({ resolver: zodResolver(SsoSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    const res = await fetch('/web-api/auth/sso', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setServerError(body.message ?? 'SSO request failed');
      return;
    }

    const payload = await res.json().catch(() => ({}));
    const url = typeof payload.url === 'string' && payload.url.trim() ? payload.url : '';
    if (!url) {
      setServerError('Invalid SSO response');
      return;
    }
    // eslint-disable-next-line react-hooks/immutability
    window.location.href = url;
  });

  return (
    <section className={styles.formBody}>
      <div className={styles.formTitle}>
        <h1 className={styles.formTitleHeading}>Login via SSO</h1>
        <p className={styles.formSubtitle}>Please enter your email address</p>
      </div>

      <form onSubmit={onSubmit} noValidate className={styles.ssoForm}>
        <div className={styles.formInputs}>
          <FormField
            className={styles.formField}
            hasError={Boolean(errors.email)}
            error={errors.email?.message}
            label="Email address"
          >
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Email address..."
              aria-label="Email address"
              className={styles.formInput}
              {...register('email')}
            />
          </FormField>
        </div>
        {serverError ? (
          <div role="alert" className={styles.serverError}>
            <MessageBar variant="negative" shape="note" icon="triangle-alert">
              {serverError}
            </MessageBar>
          </div>
        ) : null}
        <Button
          type="submit"
          size="medium"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          className={styles.ssoBtn}
        >
          {isSubmitting ? 'Logging in...' : 'Login via SSO'}
        </Button>
      </form>

      <a className={styles.backToLogin} href="/login">
        Back
      </a>
    </section>
  );
}
